import { FC, KeyboardEvent, ChangeEvent, useState, useEffect } from "react"
import { ItemZoom } from "resha"
import { Doced, ZoomRequest } from "ramper"
import { OutlinedButton } from "dash-styled"
import { ReactComponent as FileIcon } from "icons/lib/file.svg"
import { ReactComponent as ChevronIcon } from "icons/lib/chevron_up.svg"

interface DocumentHeaderProps {
    zoom?: number
    defaultZoom?: number
    fileName?: string
    totalPages?: number
    currentPage?: number
    helpers?: Doced.DocedHelpers
    onBack?: () => void
}
export const DocumentHeader: FC<DocumentHeaderProps> = ({
    zoom,
    defaultZoom,
    fileName,
    totalPages = 0,
    currentPage = 0,
    helpers,
    onBack
}) => {
    const [pageNumber, setPageNumber] = useState<number>(currentPage)
    const [isPageValid, setPageValid] = useState<boolean>(true)

    const handlePageNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
        setPageNumber(Number(event.target.value))
    }

    const handleSetNextPage = () => {
        helpers?.setNextPage()
    }

    const handleSetPreviousPage = () => {
        helpers?.setPreviousPage()
    }

    const handleSetZoom = (params: ZoomRequest) => {
        helpers?.setZoom(params)
    }

    const handlePageKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            if (pageNumber !== currentPage) {
                if (pageNumber > totalPages || pageNumber < 1) {
                    setPageNumber(currentPage)
                } else {
                    setPageValid(true)
                    helpers?.setShowingPage(pageNumber)
                }
            }
        }
    }

    useEffect(() => {
        setPageNumber(currentPage)
    }, [currentPage])

    return (
        <div
            className="absolute top-0 left-0 bg-transparent z-10 m-6"
            style={{
                width: "calc(100% - 3rem)"
            }}
        >
            <div className="w-full flex gap-6 items-center justify-between bg-white rounded-lg px-4 py-3">
                {fileName !== undefined && (
                    <div className="items-center hidden lg:flex flex-1 2xl:w-5/12 lg:w-4/12">
                        <FileIcon className="text-rhino-970 flex-2" />
                        <span className="ml-3 text-sm truncate flex-1">
                            {fileName}
                        </span>
                    </div>
                )}

                <div className="justify-end hidden lg:flex 2xl:w-7/12 lg:w-8/12">
                    <div className="flex items-center mr-6">
                        <button
                            className="rounded border border-rhino-300 mr-1 p-0"
                            onClick={handleSetPreviousPage}
                            disabled={currentPage <= 1}
                        >
                            <ChevronIcon className="transform -rotate-90 text-smoke-610" />
                        </button>
                        <div className="text-sm rounded border border-rhino-300 px-3 text-smoke-610">
                            <input
                                className={`text-sm text-right outline-none w-auto navigation-input ${
                                    isPageValid ? "" : "border border-blood-500"
                                }`}
                                value={pageNumber}
                                onChange={handlePageNumberChange}
                                onKeyUp={handlePageKeyUp}
                                type="number"
                                min={1}
                                max={totalPages}
                            />
                            <span className="text-sm">{"/" + totalPages}</span>
                        </div>
                        <button
                            className="rounded border border-rhino-300 ml-1 p-0"
                            onClick={handleSetNextPage}
                            disabled={currentPage >= totalPages}
                        >
                            <ChevronIcon className="transform rotate-90 text-smoke-610" />
                        </button>
                    </div>
                    <OutlinedButton
                        testId="set-params-back"
                        label="Change Data"
                        className="h-8 bg-white rounded mr-2"
                        onClick={onBack}
                    />
                    {zoom !== undefined && defaultZoom !== undefined && (
                        <ItemZoom
                            showPanning
                            maxZoom={10}
                            variant="dark-outline"
                            zoom={zoom}
                            defaultZoom={defaultZoom}
                            onZoomChange={handleSetZoom}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}
